/**
 * Custom Properties Reader for Word documents
 *
 * Reads the document fingerprint (6 custom properties) that identify
 * documents managed by the Document Hub.
 */

import { DocumentFingerprint } from "../types";

// Property keys used by Document Hub
const PROPERTY_KEYS = [
  "file_db",
  "file_item_id",
  "file_drive_id",
  "file_container_id",
  "meta_db",
  "meta_document_id",
] as const;

/**
 * Check if Word API is available
 */
function isWordAvailable(): boolean {
  return typeof Word !== "undefined" && typeof Word.run === "function";
}

/**
 * Read all custom properties (fingerprint) from the current document
 */
export async function readCustomProperties(): Promise<DocumentFingerprint> {
  // If not in Word, return empty fingerprint
  if (!isWordAvailable()) {
    console.log("Word API not available, returning empty fingerprint");
    return {};
  }

  try {
    return await Word.run(async (context) => {
      console.log("Reading custom properties...");
      const properties = context.document.properties.customProperties;
      properties.load("key, value");
      await context.sync();

      console.log("Custom properties count:", properties.items.length);

      const fingerprint: DocumentFingerprint = {};

      properties.items.forEach((prop) => {
        console.log(`  Property: "${prop.key}" = "${prop.value}"`);
        const key = prop.key as keyof DocumentFingerprint;
        if (PROPERTY_KEYS.includes(key as (typeof PROPERTY_KEYS)[number])) {
          fingerprint[key] = prop.value;
        }
      });

      console.log("Fingerprint result:", JSON.stringify(fingerprint));
      return fingerprint;
    });
  } catch (error) {
    console.error("Error reading custom properties:", error);
    return {};
  }
}

/**
 * Read a specific custom property
 */
export async function getCustomProperty(key: string): Promise<string | null> {
  if (!isWordAvailable()) {
    return null;
  }

  return Word.run(async (context) => {
    const properties = context.document.properties.customProperties;
    const prop = properties.getItemOrNullObject(key);
    prop.load("value");
    await context.sync();

    if (prop.isNullObject) {
      return null;
    }

    return prop.value;
  });
}

/**
 * Check if the document has Document Hub fingerprint
 */
export async function hasFingerprint(): Promise<boolean> {
  const fingerprint = await readCustomProperties();
  return !!(fingerprint.meta_document_id || fingerprint.file_item_id);
}

/**
 * Write custom properties to the document
 * Note: Generally, you should use the Document Hub API to modify metadata
 * to ensure consistency. This is provided for special cases only.
 */
export async function writeCustomProperty(
  key: string,
  value: string
): Promise<void> {
  if (!isWordAvailable()) {
    console.warn("Word API not available, cannot write custom property");
    return;
  }

  return Word.run(async (context) => {
    const properties = context.document.properties.customProperties;
    properties.add(key, value);
    await context.sync();
  });
}
